import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PROJECTS } from "@/data/site";
import { MaskedLines, SectionTag } from "@/components/Kinetic";
import { EASE, viewport } from "@/lib/motion";

const kitkat = PROJECTS.find((p) => p.id === "kitkat");

export const Commercial = ({ onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.16, 1]);
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
    <section
      ref={ref}
      data-testid="commercial"
      className="relative border-t border-[#1f1f1f] bg-[#0a0a0a] py-20 md:py-32"
    >
      <div className="mx-auto max-w-[1700px] px-5 md:px-10">
        <SectionTag num="02 / FEATURED">SPEC / COMMERCIAL</SectionTag>

        <MaskedLines
          lines={["A COMMERCIAL,", "BUILT ON SPEC."]}
          className="max-w-5xl font-display text-4xl font-black uppercase leading-[0.88] tracking-[-0.045em] text-[#e5e5e5] md:text-[5vw]"
          testId="commercial-headline"
        />
      </div>

      <div className="relative mt-12 md:mt-20">
        <motion.div
          initial={{ clipPath: "inset(12% 6% 12% 6%)" }}
          whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
          viewport={viewport}
          transition={{ duration: 1.3, ease: EASE }}
          className="relative aspect-[4/5] w-full overflow-hidden md:aspect-[16/9]"
        >
          <motion.img
            src={kitkat.image}
            alt="KitKat spec ad — project still"
            loading="lazy"
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = e.currentTarget.src.replace(
                  "maxresdefault",
                  "hqdefault"
                );
              }
            }}
            style={{ scale, y }}
            className="absolute inset-0 h-[112%] w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-[#0a0a0a]/55" />

          <span className="absolute right-4 top-4 label border border-[#d4ff00] bg-[#0a0a0a]/70 px-3 py-1.5 text-[#d4ff00] backdrop-blur md:right-8 md:top-8">
            SPEC PROJECT
          </span>

          <div className="absolute inset-0 mx-auto flex max-w-[1700px] flex-col justify-end px-5 pb-10 md:px-10 md:pb-14">
            <span className="label mb-5 w-fit bg-[#d4ff00] px-3 py-1.5 text-[#0a0a0a]">
              SPEC / COMMERCIAL
            </span>
            <h3 className="font-display text-[9vw] font-black uppercase leading-[0.85] tracking-[-0.05em] text-[#e5e5e5] md:text-[5.2vw]">
              KITKAT
              <br />
              SPEC AD
            </h3>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#c4c4c4] md:text-base">
              {kitkat.summary}
            </p>
            <div className="mt-8 flex flex-col gap-6 border-t border-[#e5e5e5]/15 pt-6 md:flex-row md:items-end md:justify-between">
              <dl className="grid grid-cols-2 gap-x-10 gap-y-4 md:grid-cols-4">
                {[
                  ["CATEGORY", kitkat.category],
                  ["ROLE", kitkat.role],
                  ["FORMAT", kitkat.format],
                  ["TOOLS", kitkat.tools.join(" / ")],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="label text-[#a3a3a3]">{k}</dt>
                    <dd className="mt-1 text-sm text-[#e5e5e5]">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={kitkat.link}
                  target="_blank"
                  rel="noreferrer"
                  data-cursor="play"
                  data-testid="commercial-watch-kitkat"
                  className="group flex w-fit items-center gap-3 bg-[#d4ff00] px-6 py-4 transition-opacity duration-300 hover:opacity-85"
                >
                  <span className="label text-[#0a0a0a]">WATCH PROJECT ↗</span>
                </a>
                <button
                  type="button"
                  onClick={() => onOpen(kitkat)}
                  data-testid="commercial-open-kitkat"
                  data-cursor="open"
                  className="group flex w-fit items-center gap-3 border border-[#e5e5e5]/25 px-6 py-4 transition-colors duration-300 hover:border-[#e5e5e5]"
                >
                  <span className="label text-[#e5e5e5]">OPEN PROJECT</span>
                  <span className="text-[#e5e5e5] transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div className="scanlines pointer-events-none absolute inset-0 opacity-25" />
        </motion.div>
      </div>
    </section>
  );
};
